// ===== FILE: src/pages/Listing/ContractorCityPage.tsx ===== (UPDATED)
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader } from 'reactstrap';
import { Link, useParams, useLoaderData } from 'react-router-dom';
import AutoBreadCrumb from '../../Components/Common/AutoBreadCrumb';
import SEOTags from './SEOTags';
import ContractorListingGrid from './ContractorListingGrid';
import ListingPagination from './ListingPagination';
import PopularCategoriesInLocation from './PopularCategoriesInLocation';
import { useContractors } from '../../hooks/useContractors';
import { contractorAPI } from '../../services/contractorService';
import { Contractor } from '../../models/Contractor.interface';
import { buildCategoryUrl, buildCategoryLocationUrl } from '../../utils/urlUtils';
import Header from "../Home/Header";
import Footer from "../Home/Footer";

interface CityPageParams {
  state: string;
  city: string;
}

const ContractorCityPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [stateDisplay, setStateDisplay] = useState('');
  const [cityDisplay, setCityDisplay] = useState('');
  const [allContractors, setAllContractors] = useState<Contractor[]>([]);
  const [allContractorsLoading, setAllContractorsLoading] = useState(true);

  const { state, city } = useParams<{ state: string; city: string }>();
  const loaderData = useLoaderData() as CityPageParams;
  const itemsPerPage = 12;

  // Get contractors for this city and state (paginated for display)
  const {
    contractors,
    loading,
    error,
    pagination,
    setPage
  } = useContractors({
    state: loaderData.state,
    city: loaderData.city,
    page: currentPage,
    limit: itemsPerPage
  }, true);

  // Fetch ALL contractors for this location to get accurate category counts
  useEffect(() => {
    const fetchAllContractors = async () => {
      setAllContractorsLoading(true);
      try {
        console.log('🔍 Fetching all contractors for city:', {
          state: loaderData.state,
          city: loaderData.city
        });
        
        // Use the general contractors endpoint with state and city filters
        const response = await contractorAPI.getContractors({
          state: loaderData.state,
          city: loaderData.city,
          page: 1,
          limit: 10000
        });
        
        console.log('✅ Frontend: Received contractors for city:', {
          count: response.data.length,
          totalCount: response.pagination?.totalCount,
          city: loaderData.city,
          state: loaderData.state
        });
        
        setAllContractors(response.data);
      } catch (err) {
        console.error('❌ Error fetching all contractors for category counts:', err);
        // Fallback to using the paginated contractors if the fetch fails
        setAllContractors(contractors);
      } finally {
        setAllContractorsLoading(false);
      }
    };

    if (loaderData.state && loaderData.city) {
      fetchAllContractors();
    }
  }, [loaderData.state, loaderData.city]);

  // Convert slugs back to display names
  useEffect(() => {
    if (loaderData.state) {
      const stateFormatted = loaderData.state
        .replace(/-/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
      setStateDisplay(stateFormatted);
    }

    if (loaderData.city) {
      const cityFormatted = loaderData.city
        .replace(/-/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
      setCityDisplay(cityFormatted);
    }
  }, [loaderData.state, loaderData.city]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <React.Fragment>
      <Header isFullWidth={true} />
      <div className="page-content">
        <SEOTags
          title={`Contractors in ${cityDisplay}, ${stateDisplay}`}
          description={`Find qualified contractors in ${cityDisplay}, ${stateDisplay}. Browse our directory of professional contractors serving ${cityDisplay} for your project needs.`}
          schemaType="ItemList"
        />

        <Container fluid>
          <AutoBreadCrumb />

          <Row>
            <Col lg={9}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">
                    Contractors in {cityDisplay}, {stateDisplay}
                    {!loading && (
                      <span className="text-muted ms-2">
                        ({pagination.totalCount} found)
                      </span>
                    )}
                  </h4>
                </CardHeader>

                <CardBody>
                  {error ? (
                    <div className="alert alert-danger">{error}</div>
                  ) : (
                    <>
                      <ContractorListingGrid 
                        contractors={contractors} 
                        loading={loading}
                      />

                      {!loading && contractors.length === 0 && (
                        <div className="text-center py-5">
                          <h5 className="text-muted">No contractors found in {cityDisplay}, {stateDisplay}</h5>
                          <p className="text-muted">
                            Try browsing contractors in the broader state area.
                          </p>
                          <Link 
                            to={`/contractors/by-location/${loaderData.state}`}
                            className="btn btn-outline-primary"
                          >
                            View contractors in {stateDisplay}
                          </Link>
                        </div>
                      )}

                      {pagination.totalPages > 1 && (
                        <ListingPagination
                          currentPage={pagination.currentPage}
                          totalPages={pagination.totalPages}
                          onPageChange={handlePageChange}
                        />
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>

            <Col lg={3}>
              {/* Popular Categories in City - Using all contractors for accurate counts */}
              <PopularCategoriesInLocation
                contractors={allContractors}
                state={loaderData.state}
                city={loaderData.city}
                title={`Popular Services in ${cityDisplay}`}
                loading={allContractorsLoading}
              />

              {/* Browse all contractors in state */}
              <Card className="mb-4">
                <CardHeader>
                  <h5 className="card-title mb-0">Browse by State</h5>
                </CardHeader>
                <CardBody>
                  <Link 
                    to={`/contractors/by-location/${loaderData.state}`}
                    className="btn btn-outline-primary w-100"
                  >
                    View All in {stateDisplay}
                  </Link>
                </CardBody>
              </Card>

              {/* Browse all categories */}
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">Browse Categories</h5>
                </CardHeader>
                <CardBody>
                  <Link 
                    to="/contractors/categories"
                    className="btn btn-outline-secondary w-100"
                  >
                    View All Services
                  </Link>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <Footer />
    </React.Fragment>
  );
};

export default ContractorCityPage;